import { useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import styled from '@emotion/styled';
import { useSelect } from '@refinedev/antd';
import type { BaseRecord, CrudFilter } from '@refinedev/core';
import { Empty, Input, Select, Spin, Typography, type SelectProps } from 'antd';
import { AnimatePresence, motion } from 'motion/react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import type {
  DiscoveryFilter,
  DiscoveryLayout,
  DiscoveryOption,
  DiscoveryResourceSelectConfig,
  DiscoveryValueMap,
} from './context';
import FilterValue from './MobileFilters/FilterValue';

const SpinnerOverlay = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
  position: absolute;
  inset: 0;
  z-index: 1;
  background: #ffffff;
  opacity: 0.5;
  pointer-events: none;
  cursor: progress;
`;

interface ResourceSelectValueProps {
  filter: DiscoveryFilter;
  values: DiscoveryValueMap;
  setValue: (value: Array<DiscoveryOption> | undefined) => void;
  layout: DiscoveryLayout;
  popupContainer: HTMLElement | null;
}

type OptionValue = DiscoveryOption['value'];
type SelectValueType = OptionValue | Array<OptionValue> | null | undefined;
const fallbackResourceConfig: DiscoveryResourceSelectConfig = {
  resource: '__noop__',
  labelField: 'label',
  valueField: 'value',
  mode: 'single',
};

const normalizeOption = (option: { label?: unknown; value?: unknown }): DiscoveryOption => {
  const rawValue = option.value as string | number | undefined;
  const value =
    typeof rawValue === 'number'
      ? rawValue
      : rawValue !== undefined && !Number.isNaN(Number(rawValue))
        ? Number(rawValue)
        : (rawValue ?? '');

  return {
    label: (option.label as React.ReactNode) ?? rawValue ?? String(value),
    value,
  };
};

const deriveSelectedOptions = (
  rawValue: unknown,
  options: Array<DiscoveryOption>,
): Array<DiscoveryOption> => {
  if (Array.isArray(rawValue)) {
    if (!rawValue.length) return [];
    if (typeof rawValue[0] === 'object' && 'value' in (rawValue[0] as DiscoveryOption)) {
      return rawValue as Array<DiscoveryOption>;
    }

    return (rawValue as Array<string | number>).map(val => {
      const matched = options.find(option => option.value === val);
      return matched ?? { label: val, value: val };
    });
  }

  if (typeof rawValue === 'string' || typeof rawValue === 'number') {
    const matched = options.find(option => option.value === rawValue);
    return matched ? [matched] : [{ label: rawValue, value: rawValue }];
  }

  return [];
};

const ResourceSelectValue = ({
  filter,
  values,
  setValue,
  layout,
  popupContainer,
}: ResourceSelectValueProps) => {
  const config = filter.resourceConfig;
  const hasConfig = Boolean(config);
  const resourceConfig = config ?? fallbackResourceConfig;
  const [searchTerm, setSearchTerm] = useState('');

  const { selectProps, query } = useSelect<BaseRecord>({
    resource: resourceConfig.resource,
    optionLabel: item => String(item[resourceConfig.labelField] ?? ''),
    optionValue: item => String(item[resourceConfig.valueField] ?? ''),
    filters: resourceConfig.filters,
    sorters: resourceConfig.sorters,
    pagination: { pageSize: resourceConfig.pageSize ?? 50 },
    debounce: 500,
    onSearch: searchValue =>
      resourceConfig.searchField
        ? [
            {
              field: resourceConfig.searchField,
              operator: resourceConfig.searchOperator ?? 'contains',
              value: searchValue,
            } as CrudFilter,
          ]
        : [],
    queryOptions: { enabled: hasConfig },
  });

  const options = useMemo<Array<DiscoveryOption>>(() => {
    const normalized = (selectProps.options ?? []).map(option => normalizeOption(option));
    if (filter.shouldSortAlphabetically) {
      return [...normalized].sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }
    return normalized;
  }, [filter.shouldSortAlphabetically, selectProps.options]);

  if (!hasConfig) {
    return null;
  }

  const selectedOptions = deriveSelectedOptions(values[filter.key], options);
  const isMulti = resourceConfig.mode === 'multiple';
  const disabled = resourceConfig.disabledWhen ? resourceConfig.disabledWhen(values) : false;

  const handleToggleOption = (option: DiscoveryOption) => {
    if (isMulti) {
      const isAlreadySelected = selectedOptions.some(selected => selected.value === option.value);
      const nextOptions = isAlreadySelected
        ? selectedOptions.filter(selected => selected.value !== option.value)
        : [...selectedOptions, option];
      setValue(nextOptions.length ? nextOptions : undefined);
      return;
    }

    const isAlreadySelected = selectedOptions[0]?.value === option.value;
    setValue(isAlreadySelected ? undefined : [option]);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setSearchTerm(nextValue);
    selectProps.onSearch?.(nextValue);
  };

  if (layout === 'mobile') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            padding: 8,
            background: '#ffffff',
          }}
        >
          <Input
            allowClear
            placeholder={`Search ${filter.label}`}
            value={searchTerm}
            onChange={handleSearchChange}
            disabled={disabled}
            size="large"
          />
        </div>
        <div>
          <AnimatePresence>
            {query.isFetching && (
              <SpinnerOverlay
                key="spinner"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.7 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Spin
                  indicator={
                    <CircleNotchIcon className="ant-spin-dot ant-spin-dot-spin" size={24} />
                  }
                  size="large"
                />
              </SpinnerOverlay>
            )}
          </AnimatePresence>

          {options.length ? (
            <AnimatePresence>
              {options.map(option => (
                <motion.div
                  key={option.value}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  layout
                >
                  <FilterValue
                    isSelected={selectedOptions.some(selected => selected.value === option.value)}
                    label={option.label}
                    value={option.value}
                    onClick={() => handleToggleOption(option)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          ) : (
            <Empty description="No options found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </div>
        {config?.helperText && (
          <Typography.Text type="secondary">{resourceConfig.helperText}</Typography.Text>
        )}
      </div>
    );
  }

  const commonSelectProps: SelectProps<SelectValueType, DiscoveryOption> = {
    allowClear: true,
    showSearch: true,
    options,
    loading: query.isFetching,
    filterOption: false,
    disabled,
    placeholder: filter.placeholder ?? filter.label,
    style: { width: '100%' },
    getPopupContainer: () => popupContainer ?? document.body,
    onSearch: selectProps.onSearch,
  };

  if (layout === 'desktop') {
    const value: SelectValueType = isMulti
      ? selectedOptions.map(option => option.value)
      : (selectedOptions[0]?.value ?? undefined);

    return (
      <Select<SelectValueType, DiscoveryOption>
        {...commonSelectProps}
        mode={isMulti ? 'multiple' : undefined}
        value={value}
        onChange={(nextValue, option) => {
          if (isMulti) {
            const nextOptions = Array.isArray(option)
              ? option.map(normalizeOption)
              : Array.isArray(nextValue)
                ? nextValue.map(v => ({ label: v, value: v }))
                : [];
            setValue(nextOptions.length ? nextOptions : undefined);
          } else {
            if (nextValue === undefined || nextValue === null || nextValue === '') {
              setValue(undefined);
              return;
            }
            const normalized = Array.isArray(option)
              ? normalizeOption(option[0] ?? { value: nextValue })
              : normalizeOption(option ?? { value: nextValue });
            setValue([normalized]);
          }
        }}
      />
    );
  }

  return null;
};

export default ResourceSelectValue;
